/**
 * Succession Role Management Component
 * 
 * Admin interface for managing critical roles for succession planning.
 * Fetches roles from F&O positions and allows HR to mark them as critical.
 * 
 * Key concepts:
 * - ROLE: A unique job title (e.g., "Campus Sales Manager")
 * - POSITION: A specific instance of a role at a location (multiple positions per role)
 * - F&O provides: functional_area (pipeline level), job_family (department)
 */

import React, { useState, useEffect, useMemo } from 'react'
import { 
  Users, 
  Search, 
  AlertTriangle,
  Briefcase,
  ChevronDown,
  ChevronUp,
  Shield,
  RefreshCw,
  MapPin,
  Layers,
  Check,
  Building2
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { organizationService, Position } from '@/services/api/organizationService'

// F&O Role (aggregated from positions)
interface FORole {
  title: string
  positions: Position[]
  positionCount: number
  filledCount: number
  vacantCount: number
  functionalArea: string // Pipeline level from F&O
  jobFamily: string // Department
  gradeLevel: string
  isCritical: boolean
  requiresSuccessionPlan: boolean
  incumbentNames: string[]
  positionIds: number[]
}

// Pipeline level mapping from F&O functional_area values
const PIPELINE_LEVEL_MAP: Record<string, { label: string; order: number }> = {
  'Manage Self Administrative': { label: 'Manage Self (Admin)', order: 1 },
  'Manage Self Profesional': { label: 'Manage Self (Professional)', order: 1 },
  'Manage SP': { label: 'Manage Self (Professional)', order: 1 },
  'Manage SA': { label: 'Manage Self (Admin)', order: 1 },
  'Manage Others': { label: 'Manage Others', order: 2 },
  'Manage Managers': { label: 'Manage Managers', order: 3 },
  'Manage Function': { label: 'Manage Function', order: 4 },
  'Manage Business': { label: 'Manage Business', order: 5 },
}

const getPipelineLabel = (functionalArea: string): string => {
  return PIPELINE_LEVEL_MAP[functionalArea]?.label || functionalArea || 'Unknown'
}

const getPipelineOrder = (functionalArea: string): number => {
  return PIPELINE_LEVEL_MAP[functionalArea]?.order || 0
}

const SuccessionRoleManagement: React.FC = () => {
  const [positions, setPositions] = useState<Position[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [departmentFilter, setDepartmentFilter] = useState<string>('all')
  const [pipelineFilter, setPipelineFilter] = useState<string>('all')
  const [showCriticalOnly, setShowCriticalOnly] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [savingRoles, setSavingRoles] = useState<Set<string>>(new Set())

  // Fetch positions from F&O
  useEffect(() => {
    fetchPositions()
  }, [])

  const fetchPositions = async () => {
    setLoading(true)
    try {
      const data = await organizationService.listPositions({
        status: ['ACTIVE', 'VACANT'],
        limit: 5000
      })
      setPositions(data)
    } catch (error) {
      console.error('Error fetching positions:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchPositions()
    setRefreshing(false)
  }

  // Aggregate positions into roles
  const roles = useMemo(() => {
    const roleMap = new Map<string, FORole>()
    
    positions.forEach(pos => {
      const title = pos.title?.trim()
      if (!title) return
      
      if (!roleMap.has(title)) {
        roleMap.set(title, {
          title,
          positions: [],
          positionCount: 0,
          filledCount: 0,
          vacantCount: 0,
          functionalArea: pos.functional_area || '',
          jobFamily: pos.job_family || '',
          gradeLevel: pos.grade_level || '',
          isCritical: false,
          requiresSuccessionPlan: false,
          incumbentNames: [],
          positionIds: []
        })
      }
      
      const role = roleMap.get(title)!
      role.positions.push(pos)
      role.positionCount++
      role.positionIds.push(pos.id)
      
      if (pos.status === 'ACTIVE' && pos.incumbent_user_id) {
        role.filledCount++
      } else if (pos.status === 'VACANT' || !pos.incumbent_user_id) {
        role.vacantCount++
      }
      
      if (pos.is_critical_role) role.isCritical = true
      if (pos.requires_succession_plan) role.requiresSuccessionPlan = true
      
      // Use functional_area and job_family from first position with data
      if (!role.functionalArea && pos.functional_area) {
        role.functionalArea = pos.functional_area
      }
      if (!role.jobFamily && pos.job_family) {
        role.jobFamily = pos.job_family
      }
      if (!role.gradeLevel && pos.grade_level) {
        role.gradeLevel = pos.grade_level
      }
    })
    
    return Array.from(roleMap.values()).sort((a, b) => {
      // Sort by pipeline level (higher first), then by title
      const orderDiff = getPipelineOrder(b.functionalArea) - getPipelineOrder(a.functionalArea)
      if (orderDiff !== 0) return orderDiff
      return a.title.localeCompare(b.title)
    })
  }, [positions])

  // Get unique departments and pipeline levels for filters
  const departments = useMemo(() => {
    const depts = new Set<string>()
    roles.forEach(r => r.jobFamily && depts.add(r.jobFamily))
    return Array.from(depts).sort()
  }, [roles])

  const pipelineLevels = useMemo(() => {
    const levels = new Set<string>()
    roles.forEach(r => r.functionalArea && levels.add(r.functionalArea))
    return Array.from(levels).sort((a, b) => getPipelineOrder(b) - getPipelineOrder(a))
  }, [roles])

  // Filter roles
  const filteredRoles = useMemo(() => {
    return roles.filter(role => {
      const matchesSearch = 
        role.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        role.jobFamily?.toLowerCase().includes(searchQuery.toLowerCase())
      
      const matchesDepartment = departmentFilter === 'all' || role.jobFamily === departmentFilter
      const matchesPipeline = pipelineFilter === 'all' || role.functionalArea === pipelineFilter
      const matchesCritical = !showCriticalOnly || role.isCritical || role.requiresSuccessionPlan
      
      return matchesSearch && matchesDepartment && matchesPipeline && matchesCritical
    })
  }, [roles, searchQuery, departmentFilter, pipelineFilter, showCriticalOnly])

  // Stats
  const stats = useMemo(() => ({
    totalRoles: roles.length,
    totalPositions: positions.length,
    criticalRoles: roles.filter(r => r.isCritical).length,
    rolesWithSuccessionPlan: roles.filter(r => r.requiresSuccessionPlan).length,
    vacantPositions: positions.filter(p => p.status === 'VACANT' || !p.incumbent_user_id).length,
    leadershipRoles: roles.filter(r => getPipelineOrder(r.functionalArea) >= 2).length
  }), [roles, positions])

  // Toggle succession planning flag
  const toggleSuccessionPlan = async (role: FORole) => {
    const newValue = !role.requiresSuccessionPlan
    setSavingRoles(prev => new Set(prev).add(role.title))
    
    try {
      // Update all positions with this role title
      await Promise.all(
        role.positionIds.map(posId => 
          organizationService.updatePosition(posId, { requires_succession_plan: newValue })
        )
      )
      
      // Refresh data
      await fetchPositions()
    } catch (error) {
      console.error('Error updating succession plan flag:', error)
    } finally {
      setSavingRoles(prev => {
        const next = new Set(prev)
        next.delete(role.title)
        return next
      })
    }
  }

  // Toggle critical role flag
  const toggleCriticalRole = async (role: FORole) => {
    const newValue = !role.isCritical
    setSavingRoles(prev => new Set(prev).add(role.title))
    
    try {
      // Update all positions with this role title
      await Promise.all(
        role.positionIds.map(posId => 
          organizationService.updatePosition(posId, { is_critical_role: newValue })
        )
      )
      
      // Refresh data
      await fetchPositions()
    } catch (error) {
      console.error('Error updating critical role flag:', error)
    } finally {
      setSavingRoles(prev => {
        const next = new Set(prev)
        next.delete(role.title)
        return next
      })
    }
  }

  // Toggle row expansion
  const toggleRowExpansion = (roleTitle: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(roleTitle)) {
        next.delete(roleTitle)
      } else {
        next.add(roleTitle)
      }
      return next
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Succession Planning - Roles</h2>
          <p className="text-gray-600 mt-1">
            Mark critical roles from F&O for succession planning. Data synced from Finance & Operations.
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={cn("h-4 w-4 mr-2", refreshing && "animate-spin")} />
          Refresh from F&O
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Roles</p>
                <p className="text-xl font-bold">{stats.totalRoles}</p>
              </div>
              <Briefcase className="h-6 w-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total Positions</p>
                <p className="text-xl font-bold">{stats.totalPositions}</p>
              </div>
              <MapPin className="h-6 w-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Critical Roles</p>
                <p className="text-xl font-bold text-red-600">{stats.criticalRoles}</p>
              </div>
              <Shield className="h-6 w-6 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Succession Plan</p>
                <p className="text-xl font-bold text-amber-600">{stats.rolesWithSuccessionPlan}</p>
              </div>
              <Users className="h-6 w-6 text-amber-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Vacant Positions</p>
                <p className="text-xl font-bold">{stats.vacantPositions}</p>
              </div>
              <AlertTriangle className="h-6 w-6 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Leadership</p>
                <p className="text-xl font-bold">{stats.leadershipRoles}</p>
              </div>
              <Layers className="h-6 w-6 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-gray-500 mb-1">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search roles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-[180px]">
              <Label className="text-xs text-gray-500 mb-1">Function</Label>
              <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Functions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Functions</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Label className="text-xs text-gray-500 mb-1">Pipeline Level</Label>
              <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  {pipelineLevels.map(level => (
                    <SelectItem key={level} value={level}>{getPipelineLabel(level)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch
                id="showCritical"
                checked={showCriticalOnly}
                onCheckedChange={setShowCriticalOnly}
              />
              <Label htmlFor="showCritical" className="text-sm cursor-pointer">
                Critical/Succession only
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Roles Table */}
      <Card>
        <CardContent className="pt-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              <RefreshCw className="h-8 w-8 mx-auto mb-2 animate-spin text-gray-400" />
              Loading roles from F&O...
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No roles found matching your filters</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Role Title</TableHead>
                  <TableHead>Function</TableHead>
                  <TableHead>Pipeline Level</TableHead>
                  <TableHead className="text-center">Positions</TableHead>
                  <TableHead className="text-center">Vacant</TableHead>
                  <TableHead className="text-center">Critical</TableHead>
                  <TableHead className="text-center">Succession</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRoles.map(role => (
                  <React.Fragment key={role.title}>
                    <TableRow 
                      className={cn(
                        "cursor-pointer hover:bg-gray-50",
                        (role.isCritical || role.requiresSuccessionPlan) && "bg-amber-50/50"
                      )}
                    >
                      <TableCell onClick={() => toggleRowExpansion(role.title)}>
                        {expandedRows.has(role.title) ? (
                          <ChevronUp className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        )}
                      </TableCell>
                      <TableCell onClick={() => toggleRowExpansion(role.title)}>
                        <div className="font-medium">{role.title}</div>
                        {role.gradeLevel && (
                          <span className="text-xs text-gray-500">Grade: {role.gradeLevel}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Building2 className="h-3 w-3 text-gray-400" />
                          <span className="text-sm">{role.jobFamily || '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {getPipelineLabel(role.functionalArea)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <span className="font-medium">{role.positionCount}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        {role.vacantCount > 0 ? (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                            {role.vacantCount}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => toggleCriticalRole(role)}
                          disabled={savingRoles.has(role.title)}
                          className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center transition-colors",
                            role.isCritical 
                              ? "bg-red-100 text-red-600 hover:bg-red-200" 
                              : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                          )}
                        >
                          {savingRoles.has(role.title) ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : role.isCritical ? (
                            <Shield className="h-4 w-4" />
                          ) : (
                            <Shield className="h-4 w-4" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => toggleSuccessionPlan(role)}
                          disabled={savingRoles.has(role.title)}
                          className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center transition-colors",
                            role.requiresSuccessionPlan 
                              ? "bg-green-100 text-green-600 hover:bg-green-200" 
                              : "bg-gray-100 text-gray-400 hover:bg-gray-200"
                          )}
                        >
                          {savingRoles.has(role.title) ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : role.requiresSuccessionPlan ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Users className="h-4 w-4" />
                          )}
                        </button>
                      </TableCell>
                    </TableRow>
                    {expandedRows.has(role.title) && (
                      <TableRow>
                        <TableCell colSpan={8} className="bg-gray-50 p-4">
                          <div className="text-sm">
                            <h4 className="font-medium mb-2">Positions ({role.positionCount})</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {role.positions.slice(0, 9).map(pos => (
                                <div 
                                  key={pos.id} 
                                  className={cn(
                                    "p-2 rounded border",
                                    pos.status === 'VACANT' || !pos.incumbent_user_id 
                                      ? "bg-amber-50 border-amber-200" 
                                      : "bg-white border-gray-200"
                                  )}
                                >
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs text-gray-500">{pos.position_code}</span>
                                    <Badge 
                                      variant={pos.status === 'ACTIVE' && pos.incumbent_user_id ? 'default' : 'secondary'}
                                      className="text-xs"
                                    >
                                      {pos.status === 'ACTIVE' && !pos.incumbent_user_id ? 'VACANT' : pos.status}
                                    </Badge>
                                  </div>
                                  {pos.incumbent_name ? (
                                    <div className="font-medium text-gray-900 mt-1">
                                      {pos.incumbent_name}
                                    </div>
                                  ) : (
                                    <div className="text-amber-600 mt-1 italic">
                                      No incumbent
                                    </div>
                                  )}
                                  {pos.campus_code && (
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      Campus: {pos.campus_code}
                                    </div>
                                  )}
                                </div>
                              ))}
                              {role.positions.length > 9 && (
                                <div className="p-2 text-center text-gray-500 text-xs">
                                  +{role.positions.length - 9} more positions
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <Layers className="h-6 w-6 text-blue-600" />
            </div>
            <div className="text-sm">
              <h4 className="font-medium text-blue-900">Roles vs Positions</h4>
              <p className="text-blue-700 mt-1">
                <strong>Roles</strong> are unique job titles from F&O (e.g., "Campus Sales Manager"). 
                <strong> Positions</strong> are specific instances of a role at different locations or departments.
                One role can have multiple positions. Mark roles as <strong>Critical</strong> for business continuity 
                or enable <strong>Succession Planning</strong> to have people leaders nominate candidates during talent reviews.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default SuccessionRoleManagement
